import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import Notification from '../components/Notification';
import NetworkToaster from '../components/NetworkToaster';
import useNetworkStatus from '../hooks/useNetworkStatus';
import '../styles/login.css';
import Cookies from 'js-cookie';
import logo from '../assets/Tr1.png';
import { UserContext } from '../contexts/UserContext';
import { CityContext } from '../contexts/CityContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faLock, faEye, faEyeSlash, faMobile } from '@fortawesome/free-solid-svg-icons';
import { GoogleLogin } from '@react-oauth/google';
// Facebook Login removed - not needed
import { jwtDecode } from 'jwt-decode';

const Login = ({ onClose, onLoginSuccess }) => {
    const navigate = useNavigate();
    const { login } = useContext(UserContext);
    const location = useLocation();
    const [socialAuthData, setSocialAuthData] = useState(null);
    const [showMobileInput, setShowMobileInput] = useState(false);
    const { cities, isLoading: citiesLoading } = useContext(CityContext);
    const [formData, setFormData] = useState({
        phone: '',
        password: '',
        mobile_number: '',
        city_id: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [notification, setNotification] = useState({
        message: '',
        type: 'info',  // Changed from '' to 'info' as default
        header: ''
    });
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [isCheckingPhone, setIsCheckingPhone] = useState(false);
    const [phoneExists, setPhoneExists] = useState(false);
    const [isAppleAvailable, setIsAppleAvailable] = useState(false);
    const [isFacebookAvailable, setIsFacebookAvailable] = useState(false);
    const [isSocialLoginProcessing, setIsSocialLoginProcessing] = useState(false);
    
    // NetworkToaster state for network-specific errors
    const [networkToaster, setNetworkToaster] = useState({
        visible: false,
        message: '',
        type: 'warning'
    });

    const isOnline = useNetworkStatus();

    // Initialize Apple Sign-In (only on HTTPS - Apple doesn't support localhost)
    useEffect(() => {
        const isHttps = window.location.protocol === 'https:';
        
        if (window.AppleID && isHttps) {
            try {
                window.AppleID.auth.init({
                    clientId: 'com.wennze.web.service',
                    scope: 'name email',
                    redirectURI: window.location.origin,
                    usePopup: true
                });
                setIsAppleAvailable(true);
            } catch (error) {
                console.log('Apple Sign-In not available');
                setIsAppleAvailable(false);
            }
        } else if (!isHttps) {
            console.log('Apple Sign-In requires HTTPS - disabled on localhost');
            setIsAppleAvailable(false);
        }

        // Check Facebook SDK availability
        if (window.FB) {
            setIsFacebookAvailable(true);
        }
    }, []);

    const checkPhoneExists = async (phone) => {
        const cleanNumber = phone.replace(/[\s-]/g, '');
        const phoneRegex = /^[0-9]{9}$/;
        if (!phoneRegex.test(cleanNumber)) {
            return false;
        }

        setIsCheckingPhone(true);
        try {
            // Strip leading 0 if present (local format) before adding international prefix
            const normalizedNumber = cleanNumber.startsWith('0') ? cleanNumber.substring(1) : cleanNumber;
            const phoneNumber = `+243${normalizedNumber}`;
            const response = await axios.post(`${config.backendUrl}/users/check-mobile-number/`, {
                mobile_number: phoneNumber
            });

            if (response.data.exists) {
                setErrors(prev => ({
                    ...prev,
                    mobile_number: 'Ce numéro est déjà utilisé'
                }));
                setPhoneExists(true);
                return true;
            } else {
                setPhoneExists(false);
                return false;
            }
        } catch (error) {
            console.error('Error checking phone:', error);
            return false;
        } finally {
            setIsCheckingPhone(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
        
        if (name === 'mobile_number') {
            const validation = validateMobileNumber(value);
            if (!validation.isValid) {
                setErrors(prev => ({
                    ...prev,
                    mobile_number: validation.message
                }));
                setPhoneExists(false);
            } else {
                setErrors(prev => {
                    const { mobile_number, ...rest } = prev;
                    return rest;
                });
                // Check phone availability when it's 9 digits
                if (value.replace(/[\s-]/g, '').length === 9) {
                    checkPhoneExists(value.replace(/[\s-]/g, ''));
                } else {
                    setPhoneExists(false);
                }
            }
        }

        // Validate phone number
        if (name === 'phone') {
            const cleanNumber = value.replace(/[\s-]/g, '');
            if (cleanNumber.startsWith('+243')) {
                if (!/^\+243[0-9]{9}$/.test(cleanNumber)) {
                    setErrors(prevErrors => ({
                        ...prevErrors,
                        phone: "Le numéro doit être au format +243XXXXXXXXX"
                    }));
                } else {
                    setErrors(prevErrors => {
                        const { phone, ...rest } = prevErrors;
                        return rest;
                    });
                }
            } else if (cleanNumber.startsWith('0')) {
                if (!/^0[0-9]{9}$/.test(cleanNumber)) {
                    setErrors(prevErrors => ({
                        ...prevErrors,
                        phone: "Le numéro doit être au format 0XXXXXXXXX"
                    }));
                } else {
                    setErrors(prevErrors => {
                        const { phone, ...rest } = prevErrors;
                        return rest;
                    });
                }
            } else if (value.length > 0) {
                setErrors(prevErrors => ({
                        ...prevErrors,
                        phone: "Le numéro doit commencer par +243 ou 0"
                    }));
                }
        }

        // Validate password
        if (name === 'password') {
            const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d).{6,}$/;
            if (!passwordRegex.test(value)) {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    password: 'Min 6 caractères avec lettres et chiffres'
                }));
            } else {
                setErrors(prevErrors => {
                    const { password, ...rest } = prevErrors;
                    return rest;
                });
            }
        }
    };

    // Function to show network-related error messages
    const showNetworkToaster = (message, type = 'warning', duration = 6000) => {
        setNetworkToaster({
            visible: true,
            message,
            type,
            duration
        });
    };

    // Function to hide network toaster
    const hideNetworkToaster = () => {
        setNetworkToaster({
            visible: false,
            message: '',
            type: 'warning'
        });
    };

    // Network status monitoring
    const handleNetworkChange = (status, message) => {
        if (status === 'offline') {
            showNetworkToaster(message, 'error');
        } else if (status === 'online') {
            showNetworkToaster(message, 'success');
        }
    };

    const handleCancel = () => {
        setNotification({
            message: "Si vous annulez maintenant, votre inscription ne sera pas terminée. Voulez-vous vraiment annuler ?",
            type: 'warning',
            header: 'Confirmation',
            showConfirmButton: true,
            onConfirm: () => {
                // Reset form data
                setShowMobileInput(false);
                setSocialAuthData(null);
                setFormData(prev => ({
                    ...prev,
                    mobile_number: '',
                    city_id: ''
                }));

                // Clear the notification immediately
                setNotification({
                    message: '',
                    type: 'info',
                    header: ''
                });

                // Close the popup immediately
                if (onClose) {
                    onClose();
                }
            },
            onClose: () => {
                // Just close the notification when user clicks No
                setNotification({
                    message: '',
                    type: 'info',
                    header: ''
                });
            }
        });
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };

    const handleNotificationClose = () => {
        setNotification({
            message: '',
            type: 'info',  // Changed from null to 'info'
            header: ''
        });
    };

    const isFormComplete = () => {
        const cleanNumber = formData.phone.replace(/[\s-]/g, '');
        return (cleanNumber.startsWith('+243') || cleanNumber.startsWith('0')) && formData.password && !errors.phone;
    };

    // Handle Facebook login
    const handleFacebookLogin = () => {
        try {
            setIsSubmitting(true);
            setIsSocialLoginProcessing(true); // Show processing overlay
            window.FB.login((response) => {
                if (response.authResponse) {
                    const { accessToken, userID } = response.authResponse;

                    // Get user profile
                    window.FB.api('/me', { fields: 'name,email,first_name,last_name' }, (userInfo) => {
                        // Send to backend
                        axios.post(
                            `${config.backendUrl}/users/facebook-login/`,
                            {
                                access_token: accessToken,
                                user_id: userID,
                                email: userInfo.email,
                                first_name: userInfo.first_name,
                                last_name: userInfo.last_name
                            }
                        ).then((backendResponse) => {
                            if (backendResponse.data.token) {
                                handleSocialLoginSuccess(backendResponse.data);
                            } else if (backendResponse.data.requires_mobile) {
                                setSocialAuthData({
                                    ...backendResponse.data,
                                    access_token: accessToken
                                });
                                setShowMobileInput(true);
                                setIsSubmitting(false);
                                setIsSocialLoginProcessing(false); // Hide overlay when showing mobile input
                            }
                        }).catch((error) => {
                            console.error('Facebook backend error:', error);
                            setNotification({
                                message: error.response?.data?.error || 'Erreur de connexion avec Facebook',
                                type: 'error',
                                header: 'Erreur'
                            });
                            setIsSubmitting(false);
                            setIsSocialLoginProcessing(false);
                        });
                    });
                } else {
                    // User cancelled Facebook login
                    setIsSubmitting(false);
                    setIsSocialLoginProcessing(false);
                }
            }, { scope: 'email,public_profile' });
        } catch (error) {
            console.error('Facebook login error:', error);
            setNotification({
                message: 'Erreur de connexion avec Facebook',
                type: 'error',
                header: 'Erreur'
            });
            setIsSubmitting(false);
            setIsSocialLoginProcessing(false);
        }
    };

    const handleAppleLogin = async () => {
        try {
            setIsSubmitting(true);
            setIsSocialLoginProcessing(true); // Show processing overlay
            const response = await window.AppleID.auth.signIn();
            
            if (response.authorization) {
                // Send to backend
                const backendResponse = await axios.post(
                    `${config.backendUrl}/users/apple-login/`,
                    {
                        id_token: response.authorization.id_token,
                        code: response.authorization.code,
                        user: response.user
                    }
                );

                if (backendResponse.data.token) {
                    await handleSocialLoginSuccess(backendResponse.data);
                } else if (backendResponse.data.requires_mobile) {
                    // Store Apple data including the id_token for later use
                    setSocialAuthData({
                        ...backendResponse.data,
                        id_token: response.authorization.id_token
                    });
                    setShowMobileInput(true);
                    setIsSubmitting(false);
                    setIsSocialLoginProcessing(false); // Hide overlay when showing mobile input
                }
            }
        } catch (error) {
            console.error('Apple login error:', error);
            setNotification({
                message: error.response?.data?.error || 'Erreur de connexion avec Apple',
                type: 'error',
                header: 'Erreur'
            });
        } finally {
            setIsSubmitting(false);
            setIsSocialLoginProcessing(false); // Always hide processing overlay
        }
    };

    const handleGoogleSuccess = async (credentialResponse) => {

        // Check network status before making request
        if (!isOnline) {
            showNetworkToaster('Aucune connexion internet détectée. Veuillez vérifier votre réseau.', 'error');
            return;
        }

        try {
            setIsSubmitting(true);
            setIsSocialLoginProcessing(true); // Show processing overlay
            const decoded = jwtDecode(credentialResponse.credential);

            const response = await axios.post(`${config.backendUrl}/users/google-login/`, {
                token: credentialResponse.credential,
                email: decoded.email,
                first_name: decoded.given_name,
                last_name: decoded.family_name
            }, {
                timeout: 30000 // 30 second timeout for slow networks
            });

            if (response.data.requires_mobile) {
                // Need mobile number
                setSocialAuthData({
                    auth_provider: 'google',
                    token: credentialResponse.credential,
                    email: decoded.email,
                    first_name: decoded.given_name,
                    last_name: decoded.family_name
                });
                setShowMobileInput(true);
                setIsSubmitting(false); // Reset isSubmitting here
            } else {
                handleSocialLoginSuccess(response.data);
            }
        } catch (error) {
            console.error('Google login error:', error);
            
            let errorMessage = "Échec de la connexion avec Google";
            let useNetworkToaster = false;
            
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                errorMessage = "La connexion Google prend trop de temps. Vérifiez votre connexion internet.";
                useNetworkToaster = true;
            } else if (error.request) {
                errorMessage = "Impossible de contacter le serveur pour la connexion Google. Vérifiez votre connexion internet.";
                useNetworkToaster = true;
            } else if (error.response) {
                errorMessage = error.response.data?.error || "Échec de la connexion avec Google";
            }
            
            if (useNetworkToaster) {
                showNetworkToaster(errorMessage, 'error', 8000); // 8 seconds for timeout errors
            } else {
                setNotification({
                    message: errorMessage,
                    type: 'error',
                    header: 'Erreur'
                });
            }
            setIsSubmitting(false); // Reset isSubmitting on error
        }
    };

    const handleMobileSubmit = async (e) => {
        e.preventDefault();

        const cleanNumber = formData.mobile_number.replace(/[\s-]/g, '');
        if (!cleanNumber.match(/^[0-9]{9}$/)) {
            setNotification({
                message: "Le numéro de mobile doit être un numéro valide (9 chiffres).",
                type: 'error',
                header: 'Erreur'
            });
            return;
        }

        if (phoneExists) {
            setNotification({
                message: "Ce numéro est déjà utilisé. Veuillez vous connecter ou utiliser un autre numéro.",
                type: 'error',
                header: 'Erreur'
            });
            return;
        }

        if (!agreedToTerms) {
            setErrors(prev => ({
                ...prev,
                terms: 'Vous devez accepter les termes et conditions'
            }));
            return;
        }

        try {
            setIsSubmitting(true);
            
            // Check network status before making request
            if (!isOnline) {
                showNetworkToaster('Aucune connexion internet détectée. Veuillez vérifier votre réseau.', 'error');
                setIsSubmitting(false);
                return;
            }

            // Strip leading 0 if present (local format) before adding international prefix
            const normalizedNumber = cleanNumber.startsWith('0') ? cleanNumber.substring(1) : cleanNumber;

            const payload = {
                auth_provider: socialAuthData.auth_provider,
                mobile_number: `+243${normalizedNumber}`,
                city_id: formData.city_id,
                email: socialAuthData.email,
                first_name: socialAuthData.first_name,
                last_name: socialAuthData.last_name
            };

            // Debug: log payload to identify missing fields
            console.log('Complete social signup payload:', payload);
            console.log('socialAuthData:', socialAuthData);

            // Add the appropriate token based on provider
            if (socialAuthData.auth_provider === 'google') {
                payload.token = socialAuthData.token;
            } else if (socialAuthData.auth_provider === 'apple') {
                payload.id_token = socialAuthData.id_token;
            } else if (socialAuthData.auth_provider === 'facebook') {
                payload.access_token = socialAuthData.access_token;
            }

            try {
                const response = await axios.post(
                    `${config.backendUrl}/users/complete-social-signup/`,
                    payload,
                    {
                        timeout: 30000 // 30 second timeout for slow networks
                    }
                );

                if (response.data.user_id) {
                    setNotification({
                        message: 'Un code de vérification a été envoyé à votre numéro de mobile.',
                        type: 'success',
                        header: 'Succès'
                    });

                    if (onClose) {
                        onClose();
                    }

                    navigate(`/verify-user/${response.data.user_id}`);
                }
            } catch (error) {
                if (error.response?.status === 409) {
                    // Handle both email and mobile number conflicts
                    if (error.response.data.email_exists) {
                        setNotification({
                            message: "Un compte existe déjà avec cet email. Veuillez vous connecter ou cliquer sur 'Mot de passe oublié'.",
                            type: 'warning',
                            header: 'Compte existant',
                        });
                        // Close modal and show login
                        if (onClose) {
                            onClose();
                        }
                    } else if (error.response.data.account_exists) {
                        setNotification({
                            message: "Un compte existe déjà avec ce numéro de téléphone. Si vous avez oublié votre mot de passe, veuillez cliquer sur 'Mot de passe oublié'.",
                            type: 'warning',
                            header: 'Compte existant',
                        });
                    }
                } else {
                    let errorMessage = "Échec de la soumission du numéro de mobile";
                    let useNetworkToaster = false;
                    
                    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                        errorMessage = "La soumission du numéro prend trop de temps. Vérifiez votre connexion internet.";
                        useNetworkToaster = true;
                    } else if (error.request) {
                        errorMessage = "Impossible de contacter le serveur. Vérifiez votre connexion internet.";
                        useNetworkToaster = true;
                    } else if (error.response) {
                        errorMessage = error.response.data?.error || "Échec de la soumission du numéro de mobile";
                    }
                    
                    if (useNetworkToaster) {
                        showNetworkToaster(errorMessage, 'error', 8000); // 8 seconds for timeout errors
                    } else {
                        setNotification({
                            message: errorMessage,
                            type: 'error',
                            header: 'Erreur'
                        });
                    }
                }
            }
        } catch (error) {
            console.error('Error submitting mobile number:', error);
            
            let errorMessage = "Échec de la soumission du numéro de mobile";
            let useNetworkToaster = false;
            
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                errorMessage = "La soumission du numéro prend trop de temps. Vérifiez votre connexion internet.";
                useNetworkToaster = true;
            } else if (error.request) {
                errorMessage = "Impossible de contacter le serveur. Vérifiez votre connexion internet.";
                useNetworkToaster = true;
            } else if (error.response) {
                errorMessage = error.response.data?.error || "Échec de la soumission du numéro de mobile";
            }
            
            if (useNetworkToaster) {
                showNetworkToaster(errorMessage, 'error');
            } else {
                setNotification({
                    message: errorMessage,
                    type: 'error',
                    header: 'Erreur'
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Common handler for social login success
    const handleSocialLoginSuccess = async (data) => {
        try {
            const { token, user, requires_verification } = data;

            // Set token in cookies with 30-day expiry (persistent login)
            Cookies.set('authToken', token, { expires: 30 });

            // Use login function from UserContext
            const loggedInUser = await login(user);

            if (loggedInUser) {
                if (requires_verification) {
                    setNotification({
                        message: 'Un code de vérification a été envoyé à votre numéro de mobile.',
                        type: 'info',
                        header: 'Vérification requise'
                    });
                    const userIdentifier = loggedInUser.uuid || loggedInUser.id;

                    // Close modal before navigation
                    if (onClose) {
                        onClose();
                    }

                    navigate(`/verify-user/${userIdentifier}`);
                    setIsSubmitting(false);
                    setIsSocialLoginProcessing(false); // Hide processing overlay
                    return;
                }

                // Success notification
                setNotification({
                    message: 'Connexion réussie!',
                    type: 'success',
                    header: 'Succès'
                });

                // Dispatch event to update UI immediately
                window.dispatchEvent(new Event('userStateChanged'));

                // Use setTimeout to ensure state updates propagate
                setTimeout(() => {
                    if (onLoginSuccess) {
                        onLoginSuccess(loggedInUser);
                    }

                    if (onClose) {
                        onClose();
                    }

                    // Only navigate if onLoginSuccess is not provided (standalone page usage)
                    if (!onLoginSuccess) {
                        const redirectPath = location.state?.from || '/';
                        navigate(redirectPath);
                    }
                }, 100);
            }
        } catch (error) {
            console.error('Error in handleSocialLoginSuccess:', error);
            setNotification({
                message: 'Erreur lors de la connexion',
                type: 'error',
                header: 'Erreur'
            });
        } finally {
            setIsSubmitting(false);
            setIsSocialLoginProcessing(false); // Always hide processing overlay
        }
    };
    // Handle regular login submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Check network status before making request
        if (!isOnline) {
            showNetworkToaster('Aucune connexion internet détectée. Veuillez vérifier votre réseau.', 'error');
            setIsSubmitting(false);
            return;
        }

        try {
            // Prepare login data with phone
            const loginData = {
                username: formData.phone, // Use phone as username for backend compatibility
                password: formData.password
            };

            // Add timeout configuration to axios - increased for slow networks
            const response = await axios.post(`${config.backendUrl}/users/login/`, loginData, {
                timeout: 30000, // 30 second timeout for slow networks
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            console.log('Login response:', response.data);

            if (response.data.token) {
                // Store token immediately with 30-day expiry (persistent login)
                Cookies.set('authToken', response.data.token, { expires: 30 });

                try {
                    // Call login with error handling
                    const loggedInUser = await login(response.data.user);

                    if (loggedInUser) {
                        setNotification({
                            message: 'Connexion réussie!',
                            type: 'success',
                            header: 'Succès'
                        });

                        // Ensure callbacks are executed properly
                        if (onLoginSuccess) {
                            try {
                                onLoginSuccess(loggedInUser);
                            } catch (callbackError) {
                                console.error('Login success callback error:', callbackError);
                            }
                        }

                        // Close popup with delay to show success message
                        setTimeout(() => {
                            if (onClose) {
                                onClose();
                            }

                            // Only navigate if onLoginSuccess is not provided (standalone page usage)
                            if (!onLoginSuccess) {
                                const redirectPath = location.state?.from || '/';
                                navigate(redirectPath);
                            }

                            window.dispatchEvent(new Event('userStateChanged'));
                        }, 500); // 500ms delay to show success message
                    } else {
                        throw new Error('Failed to process login');
                    }
                } catch (loginError) {
                    console.error('Login context error:', loginError);
                    setNotification({
                        message: 'Erreur lors de la connexion. Veuillez réessayer.',
                        type: 'error',
                        header: 'Erreur'
                    });
                }

            } else if (response.data.user_id) {
                // User needs to verify mobile number
                console.log('Redirecting to verification for user:', response.data.user_id);
                
                // Clear any existing auth data to ensure user is not logged in
                Cookies.remove('authToken');
                localStorage.removeItem('authToken');
                localStorage.removeItem('user');
                localStorage.removeItem('isLoggedIn');
                
                setNotification({
                    message: response.data.message || 'Un code de vérification a été envoyé à votre numéro.',
                    type: 'info',
                    header: 'Vérification requise'
                });
                
                setTimeout(() => {
                    if (onClose) {
                        onClose();
                    }
                    navigate(`/verify-user/${response.data.user_id}`);
                }, 1500);
            } else {
                setNotification({
                    message: response.data.error || "Échec de la connexion",
                    type: 'error',
                    header: 'Erreur'
                });
            }
        } catch (error) {
            console.error('Login error:', error);
            
            let errorMessage = "Échec de la connexion";
            let useNetworkToaster = false;
            
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                errorMessage = "La connexion prend trop de temps. Veuillez vérifier votre connexion internet.";
                useNetworkToaster = true;
            } else if (error.response) {
                errorMessage = error.response.data?.error || "Nom d'utilisateur ou mot de passe incorrect.";
            } else if (error.request) {
                errorMessage = "Impossible de contacter le serveur. Vérifiez votre connexion internet.";
                useNetworkToaster = true;
            }
            
            // Use NetworkToaster for network-specific errors, regular Notification for others
            if (useNetworkToaster) {
                showNetworkToaster(errorMessage, 'error', 8000); // 8 seconds for timeout errors
            } else {
                setNotification({
                    message: errorMessage,
                    type: 'error',
                    header: 'Erreur'
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };
    const validateMobileNumber = (number) => {
        const cleanNumber = number.replace(/[\s-]/g, '');
        // Accept 9 digits (will strip leading 0 if present)
        if (!/^[0-9]{9}$/.test(cleanNumber)) {
            return { isValid: false, message: "Le numéro doit avoir 9 chiffres" };
        }
        return { isValid: true, message: "" };
    };

    return (
        <>
        <div className="modern-login-page">
            <Notification
                message={notification.message}
                type={notification.type}
                header={notification.header}
                onClose={handleNotificationClose}
            />
            <NetworkToaster
                message={networkToaster.message}
                visible={networkToaster.visible}
                type={networkToaster.type}
                onClose={hideNetworkToaster}
                duration={networkToaster.duration || 6000}
            />
            <div className="modern-login-container">
                {/* Header */}
                <div className="modern-login-header">
                    <h1 className="modern-login-title">Bienvenue</h1>
                    <p className="modern-login-subtitle">Connectez-vous pour continuer</p>
                </div>
                {showMobileInput ? (
                    // Mobile number form for social login
                    <form onSubmit={handleMobileSubmit} style={{ padding: '20px' }}>
                        <h3 style={{ marginBottom: '20px', color: '#333', fontSize: '18px' }}>Complétez votre profil</h3>
                        
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Numéro de téléphone *</label>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ padding: '10px', backgroundColor: '#f0f0f0', border: '1px solid #ddd', borderRight: 'none', borderRadius: '4px 0 0 4px' }}>+243</span>
                                <input
                                    type="text"
                                    id="mobile_number"
                                    name="mobile_number"
                                    value={formData.mobile_number}
                                    onChange={handleChange}
                                    required
                                    disabled={isSubmitting}
                                    placeholder="999999999"
                                    maxLength={9}
                                    style={{
                                        flex: 1,
                                        padding: '10px',
                                        border: errors.mobile_number ? '1px solid red' : '1px solid #ddd',
                                        borderRadius: '0 4px 4px 0',
                                        fontSize: '14px'
                                    }}
                                />
                            </div>
                            {isCheckingPhone && (
                                <p style={{ color: '#666', fontSize: '12px', marginTop: '5px' }}>Vérification du numéro...</p>
                            )}
                            {errors.mobile_number && (
                                <p style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{errors.mobile_number}</p>
                            )}
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Ville *</label>
                            {citiesLoading ? (
                                <div style={{ padding: '10px', color: '#666' }}>Chargement des villes...</div>
                            ) : (
                                <select
                                    id="city_id"
                                    name="city_id"
                                    value={formData.city_id}
                                    onChange={handleChange}
                                    required
                                    disabled={isSubmitting}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: errors.city_id ? '1px solid red' : '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                >
                                    <option value="">Sélectionnez une ville</option>
                                    {cities.map(city => (
                                        <option key={city.id} value={city.id}>
                                            {city.name}
                                        </option>
                                    ))}
                                </select>
                            )}
                            {errors.city_id && (
                                <p style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{errors.city_id}</p>
                            )}
                        </div>
                        
                        {/* Terms and Conditions */}
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'flex', alignItems: 'start', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={agreedToTerms}
                                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                                    style={{ marginRight: '10px', marginTop: '4px' }}
                                    disabled={isSubmitting}
                                />
                                <span style={{ fontSize: '14px' }}>
                                    J'accepte les <a href="/terms-conditions/conditions-utilisation" target="_blank" style={{ color: '#003366' }}>termes et conditions</a> et la <a href="/terms-conditions/politique-confidentialite" target="_blank" style={{ color: '#003366' }}>politique de confidentialité</a>
                                </span>
                            </label>
                            {errors.terms && (
                                <p style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{errors.terms}</p>
                            )}
                        </div>
                        
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                type="button"
                                onClick={handleCancel}
                                disabled={isSubmitting}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '16px',
                                    fontWeight: '500',
                                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                                }}
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                disabled={
                                    isSubmitting ||
                                    !formData.city_id ||
                                    !formData.mobile_number ||
                                    !agreedToTerms ||
                                    phoneExists ||
                                    isCheckingPhone ||
                                    errors.mobile_number
                                }
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    backgroundColor: (
                                        isSubmitting ||
                                        !formData.city_id ||
                                        !formData.mobile_number ||
                                        !agreedToTerms ||
                                        phoneExists ||
                                        isCheckingPhone ||
                                        errors.mobile_number
                                    ) ? '#ccc' : '#003366',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '16px',
                                    fontWeight: '500',
                                    cursor: (
                                        isSubmitting ||
                                        !formData.city_id ||
                                        !formData.mobile_number ||
                                        !agreedToTerms ||
                                        phoneExists ||
                                        isCheckingPhone ||
                                        errors.mobile_number
                                    ) ? 'not-allowed' : 'pointer',
                                    opacity: (
                                        isSubmitting ||
                                        !formData.city_id ||
                                        !formData.mobile_number ||
                                        !agreedToTerms ||
                                        phoneExists ||
                                        isCheckingPhone ||
                                        errors.mobile_number
                                    ) ? 0.6 : 1
                                }}
                            >
                                {isSubmitting ? 'Chargement...' : isCheckingPhone ? 'Vérification...' : 'Continuer'}
                            </button>
                        </div>
                    </form>
                ) : (
                    <>
                        <form className="modern-login-form" onSubmit={handleSubmit}>
                            {/* Phone Input */}
                            <div className="modern-input-group">
                                <label className="modern-input-label">Numéro de téléphone</label>
                                <div className={`modern-input-container ${errors.phone ? 'error' : ''}`}>
                                    <FontAwesomeIcon icon={faMobile} className="input-icon" />
                                    <input
                                        type="text"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        required
                                        placeholder="+243xxxxxxxxx ou 0xxxxxxxxx"
                                        className="modern-input"
                                        disabled={isSubmitting}
                                    />
                                </div>
                                {errors.phone && (
                                    <div className="modern-error-message">{errors.phone}</div>
                                )}
                            </div>

                            {/* Password Input */}
                            <div className="modern-input-group">
                                <label className="modern-input-label">Mot de passe</label>
                                <div className={`modern-input-container ${errors.password ? 'error' : ''}`}>
                                    <FontAwesomeIcon icon={faLock} className="input-icon" />
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                        placeholder="Entrez votre mot de passe"
                                        className="modern-input"
                                        disabled={isSubmitting}
                                    />
                                    <FontAwesomeIcon
                                        icon={showPassword ? faEyeSlash : faEye}
                                        className="password-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                    />
                                </div>
                                {errors.password && (
                                    <div className="modern-error-message">{errors.password}</div>
                                )}
                            </div>

                            {/* Login Button */}
                            <button
                                type="submit"
                                className={`modern-login-button ${!isFormComplete() ? 'disabled' : ''}`}
                                disabled={isSubmitting || !isFormComplete()}
                            >
                                {isSubmitting ? 'Connexion...' : 'Se connecter'}
                            </button>
                        </form>
                        <div style={{ textAlign: 'center', margin: '15px 0' }}>
                            <a href="/forgot-password" style={{ color: '#003366', textDecoration: 'none', fontSize: '14px' }}>Mot de passe oublié?</a>
                        </div>
                        
                        {/* Divider */}
                        <div className="modern-divider">
                            <div className="modern-divider-line"></div>
                            <span className="modern-divider-text">OU</span>
                            <div className="modern-divider-line"></div>
                        </div>
                        
                        {/* Social Login Options */}
                        <div className="modern-social-section">
                            <div className="modern-social-buttons">
                                <GoogleLogin
                                    onSuccess={handleGoogleSuccess}
                                    onError={() => {
                                        setNotification({
                                            message: "Échec de la connexion avec Google",
                                            type: 'error',
                                            header: 'Erreur'
                                        });
                                    }}
                                    theme="outline"
                                    size="large"
                                    text="continue_with"
                                    locale="fr"
                                    width="100%"
                                />
                                
                                {isAppleAvailable && (
                                    <button
                                        type="button"
                                        className="modern-social-button apple-button"
                                        onClick={handleAppleLogin}
                                        disabled={isSubmitting}
                                    >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                                    </svg>
                                    Continuer avec Apple
                                </button>
                                )}
                                
                                {isFacebookAvailable && (
                                <button
                                    type="button"
                                    className="modern-social-button facebook-button"
                                    onClick={handleFacebookLogin}
                                    disabled={isSubmitting}
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                    </svg>
                                    Continuer avec Facebook
                                </button>
                                )}
                            </div>
                        </div>
                
                {/* Footer */}
                <div className="modern-login-footer">
                    <div className="forgot-password-link">
                        <a href="/forgot-password">Mot de passe oublié?</a>
                    </div>
                    <div className="register-link">
                        <span>Nouveau sur Wennze? </span>
                        <a href="/register">Créez votre compte</a>
                    </div>
                </div>
                    </>
                )}

            </div>
            
            {/* Social Login Processing Overlay */}
            {isSocialLoginProcessing && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 10000,
                    flexDirection: 'column'
                }}>
                    <div style={{
                        width: '50px',
                        height: '50px',
                        border: '4px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '4px solid #fff',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    <p style={{
                        color: '#fff',
                        marginTop: '16px',
                        fontSize: '16px',
                        textAlign: 'center'
                    }}>Authentification en cours...</p>
                </div>
            )}
        </div>
        
        <style jsx>{`
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `}</style>
        </>
    );
};

export default Login;